import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppDialog } from "components/common/dialogs";
import { Box } from "@mui/material";
import { SaveOutlined } from "@mui/icons-material";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { AppButton } from "components/common/buttons";
import {
  AppAutocomplete, AppSelect, AppTextField, shouldUseAutocomplete,
} from "components/common/inputs";
import dayjs from "dayjs";

export default function EntityFormDialog({
  open,
  onClose,
  title,
  fields = [],
  initialData = {},
  submitLabel = "Lưu",
  onSubmit,
  transformForm,
  maxWidth = "sm",
  children,
  topContent,
}) {
  const [form, setForm] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [submitting, setSubmitting] = useState(false);
  const initializedOpenRef = useRef(false);

  const buildInitialForm = useCallback((dataSource = {}) => {
    const data = { ...dataSource };
    const baseForm = transformForm ? transformForm(data) : data;
    const initialFields = typeof fields === "function" ? fields(baseForm) : fields;

    initialFields.forEach((field) => {
      if (
        field.type === "date" &&
        field.defaultToday &&
        !data[field.name]
      ) {
        data[field.name] = dayjs().format("YYYY-MM-DD");
      }
    });

    return data;
  }, [fields, transformForm]);

  useEffect(() => {
    if (!open) {
      initializedOpenRef.current = false;
      return;
    }
    if (!initializedOpenRef.current) {
      initializedOpenRef.current = true;
      setForm(buildInitialForm(initialData || {}));
      setErrors({});
      setSnackbar({ open: false, message: "", severity: "info" });
    }
  }, [buildInitialForm, initialData, open]);

  const resolvedForm = useMemo(() => transformForm ? transformForm(form) : form, [form, transformForm]);
  const resolvedFields = useMemo(
    () => (typeof fields === "function" ? fields(resolvedForm) : fields),
    [fields, resolvedForm],
  );

  const update = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === "sectionId" ? { scopeId: "", itemId: "" } : {}),
      ...(name === "scopeId" ? { itemId: "" } : {}),
    }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const submit = async () => {
    if (submitting) return;
    const validation = {};
    resolvedFields.forEach((field) => {
      const value = resolvedForm[field.name];
      if (field.required && (value === "" || value === null || value === undefined)) {
        validation[field.name] = `${field.label} là bắt buộc`;
      }
      if (field.validate) {
        const message = field.validate(value, resolvedForm);
        if (message) validation[field.name] = message;
      }
    });
    setErrors(validation);
    if (Object.keys(validation).length) {
      setSnackbar({ open: true, message: "Vui lòng kiểm tra các trường thông tin chưa hợp lệ.", severity: "warning" });
      return;
    }

    setSubmitting(true);
    setSnackbar((current) => ({ ...current, open: false }));
    try {
      const result = await onSubmit?.(resolvedForm);
      if (result === false) return;
      onClose?.();
    } catch (error) {
      const responseData = error.response?.data;
      const serverErrors = responseData?.errors || responseData?.Errors || {};
      const validation = {};
      resolvedFields.forEach((field) => {
        const key = Object.keys(serverErrors).find((name) => name.toLowerCase() === field.name.toLowerCase());
        if (!key) return;
        const messages = serverErrors[key];
        validation[field.name] = Array.isArray(messages) ? messages.join(" ") : messages;
      });
      setErrors(validation);
      const validationMessage = Object.values(serverErrors).flat().filter((message) => typeof message === "string").join(" ");
      setSnackbar({
        open: true,
        message: validationMessage || responseData?.message || responseData?.Message || error.message || "Không thể lưu dữ liệu.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <AppDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={maxWidth}
      className="subcontract-dialog"
      title={title}
      loading={submitting}
      actions={
        <>
          <AppButton variant="outlined" color="inherit" onClick={onClose} disabled={submitting}>Hủy</AppButton>
          <AppButton variant="contained" startIcon={<SaveOutlined />} onClick={submit} disabled={submitting}>
            {submitting ? "Đang lưu..." : submitLabel}
          </AppButton>
        </>
      }
    >
      {typeof topContent === "function" ? topContent(resolvedForm) : topContent}
      <Box className="subcontract-form-grid">
        {resolvedFields.map((field) => {
          const value = resolvedForm[field.name] ?? "";
          if (field.options) {
            const Dropdown = shouldUseAutocomplete(field.options, field.autocomplete)
              ? AppAutocomplete
              : AppSelect;
            return <Dropdown
              key={field.name}
              className={field.full ? "full" : ""}
              label={field.label}
              value={value}
              options={field.options}
              required={field.required}
              disabled={field.disabled || submitting}
              error={Boolean(errors[field.name])}
              helperText={errors[field.name] || field.helperText}
              placeholder={field.placeholder}
              allowClear={!field.required}
              onChange={(selectedValue) => update(field.name, selectedValue)}
            />;
          }

          return (
            <AppTextField
              key={field.name}
              className={field.full ? "full" : ""}
              label={field.label}
              required={field.required}
              type={field.type || "text"}
              defaultToday={field.defaultToday}
              multiline={field.multiline}
              minRows={field.multiline ? field.minRows || 3 : undefined}
              value={value}
              disabled={field.disabled || submitting}
              error={Boolean(errors[field.name])}
              helperText={errors[field.name] || field.helperText}
              inputProps={field.inputProps}
              onChange={(nextValue) => update(field.name, nextValue)}
            />
          );
        })}
        {children && (
          <div className="full">
            {children}
          </div>
        )}

      </Box>

    </AppDialog>
    <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
    />
    </>
  );
}
